import { SubscriptionApi } from "@features/shared/api/subscription";
import { StudentApi, UserApi } from "@features/shared/api/course-data.api";
import { SolidUpdateCardFormResponse } from "@features/shared/entities/payment";
import { ISubscription, PauseSubscriptionRequest } from "@features/shared/entities/subscription";
import {
    restoreCancelClicked,
    restoreConfirmClicked,
    restoreConfirmationPresented,
    subscriptionCanceled,
    subscriptionRestoreClicked,
    subscriptionRestored,
    type RestoreStepPayload
} from "@features/shared/events";
import { setGeoUserCountry, setKnownUserCountry } from "@features/helpers";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { HttpRequest } from "@features/shared/request/model";
import { IUserAuthContract } from "@features/student/auth/model";
import { logoutButtonClicked } from "@features/student/auth/ui/sign-in.form";
import { createEffect, createEvent, createStore, sample, combine } from "effector";
import { interval, delay } from "patronum";
import { appMounted, routePathNameChanged, $activeRoute } from "@features/app/model";
import { routerReplace } from "@features/shared/router";
import {
    isPausedSubscriptionAllowedPath,
    isPausedSubscriptionStatus,
    PAUSED_SUBSCRIPTION_IN_APP_REDIRECT_PATH,
    PAUSED_SUBSCRIPTION_REDIRECT_PATH
} from "@features/subscription/paused-gate";
import {
    type RestoreStatus,
    isMandateRevokedError,
    isRestoreConfirmed,
    restoreTimeoutStatus
} from "@features/subscription/restore-outcome";
import type { RestoreSubscriptionResponse } from "@features/subscription/restore-outcome";
import {
    restoreTermsOf,
    type RestorePreview,
    type RestoreState,
    type RestoreTerms
} from "@features/subscription/restore-confirmation";

export type { RestoreStatus };


const getSubscriptionPauseErrorMessage = (error: unknown): string => {
    if (typeof error === "string") return error;

    if (error && typeof error === "object") {
        if ("error" in error && typeof error.error === "string") return error.error;
        if ("message" in error && typeof error.message === "string") return error.message;
    }

    return "Something went wrong";
};

const manageFfSubscriptionClicked = createEvent();
const cancelSubscriptionButtonClicked = createEvent<string>();
const cancelSubscriptionWithReasonsClicked = createEvent<{ subscriptionId: string; reasons: string[] }>();
const restoreSubscriptionButtonClicked = createEvent<string>();
const pauseSubscriptionClicked = createEvent<PauseSubscriptionRequest>();
const resumePausedSubscriptionClicked = createEvent();
const subscriptionPauseErrorDismissed = createEvent();
const restoreDialogClosed = createEvent();
const restoreFailureDetected = createEvent();
// T09: the Restore button only opens a confirmation screen (restoreSubscriptionRequested);
// the request goes out from that screen's own button, restoreSubscriptionButtonClicked.
const restoreSubscriptionRequested = createEvent<string>();
const restoreConfirmationDismissed = createEvent();
const setPaymentSystem = createEvent<string>();
export const subscriptionScreenOpened = createEvent();
const startRestorePolling = createEvent<string>();
const stopRestorePolling = createEvent();
const startPauseStatusPolling = createEvent<"paused" | "active">();
const stopPauseStatusPolling = createEvent();

const changePaymentMethodClicked = createEvent<string>();
const changePaymentMethodModalClosed = createEvent();
const changePaymentMethodSuccess = createEvent();
const changePaymentMethodFailed = createEvent<string>();
const widgetPaymentSuccess = createEvent();
const solidgateUpsellPaymentCapabilityRequested = createEvent();

const getSolidgateUpsellPaymentCapabilityFx = createEffect(async () => {
    return await SubscriptionApi.getSolidgateUpsellPaymentCapability();
});

const $isSolidgateUpsellPaymentCapabilityPending = getSolidgateUpsellPaymentCapabilityFx.pending;

export type ChangePaymentMethodStatus = "idle" | "loading" | "form_ready" | "polling" | "success" | "error";

const $subscriptions = createStore<ISubscription[]>([]);
const $isPausedSubscriptionGateActive = createStore(false).reset(logoutButtonClicked);
const $hasSubscriptionByEmail = createStore<boolean | null>(null);
const $subscriptionCheckCompleted = createStore<boolean>(false);
const $subscriptionEndDate = createStore("").reset(logoutButtonClicked);
const $paymentSystem = createStore("")
    .on(setPaymentSystem, (_, p) => p)
    .reset(logoutButtonClicked);

const $solidgateUpsellHasRecurringToken = createStore<boolean | null>(null)
    .on(getSolidgateUpsellPaymentCapabilityFx.doneData, (_, response) => response.hasRecurringToken)
    .reset([changePaymentMethodSuccess, logoutButtonClicked]);

const $solidgateUpsellPaymentCapabilityError = createStore<string | null>(null)
    .on(getSolidgateUpsellPaymentCapabilityFx.failData, (_, error) =>
        error instanceof Error ? error.message : "Unable to check saved payment method"
    )
    .reset([solidgateUpsellPaymentCapabilityRequested, logoutButtonClicked]);

const $pollingSubId = createStore<string>("")
    .on(startRestorePolling, (_, subId) => subId)
    .reset(stopRestorePolling);

const $restorePollTickCount = createStore<number>(0).reset(startRestorePolling);

// Set when the polled subscription has been observed as auto-renewable during
// restore polling. A later flip back to non-renewable means Solidgate restored
// the subscription but the charge failed and it was cancelled again — that is
// a definite failure, no need to wait for the polling timeout.
const $restoreActivationSeen = createStore<boolean>(false).reset(startRestorePolling, stopRestorePolling);

const $changePaymentMethodSubscriptionId = createStore<string | null>(null)
    .on(changePaymentMethodClicked, (_, subId) => subId)
    .reset(changePaymentMethodModalClosed, logoutButtonClicked);

const $updateCardFormResponse = createStore<SolidUpdateCardFormResponse | null>(null).reset(
    changePaymentMethodModalClosed,
    logoutButtonClicked
);

const $updateCardOrderId = createStore<string | null>(null).reset(changePaymentMethodModalClosed, logoutButtonClicked);

const $updateCardPollCount = createStore<number>(0).reset(
    widgetPaymentSuccess,
    changePaymentMethodModalClosed,
    logoutButtonClicked
);

const $changePaymentMethodStatus = createStore<ChangePaymentMethodStatus>("idle")
    .on(changePaymentMethodClicked, () => "loading")
    .on(widgetPaymentSuccess, () => "polling")
    .on(changePaymentMethodSuccess, () => "success")
    .on(changePaymentMethodFailed, () => "error")
    .reset(changePaymentMethodModalClosed, logoutButtonClicked);

const $changePaymentMethodError = createStore<string | null>(null)
    .on(changePaymentMethodFailed, (_, msg) => msg)
    .reset(changePaymentMethodModalClosed, changePaymentMethodClicked, logoutButtonClicked);

const $pausedGateRouteHistory = createStore<{ prev: string; current: string }>({ prev: "/", current: "/" })
    .on(routePathNameChanged, (state, pathname) => ({ prev: state.current, current: pathname }));

const { tick: restorePollTick } = interval({
    timeout: 2000,
    start: startRestorePolling,
    stop: stopRestorePolling,
    leading: true
});

const { tick: pauseStatusPollTick } = interval({
    timeout: 2000,
    start: startPauseStatusPolling,
    stop: stopPauseStatusPolling,
    leading: false
});

const getSubscriptionsFx = createEffect(async () => {
    const response = await SubscriptionApi.getSubscriptions();
    return response;
});

// The student's provider is only known once the subscriptions have been read:
// until then $paymentSystem is "" and screens that branch on it would show the
// wrong branch first. `finally` and not `doneData`, so a failed request also
// releases the screens instead of leaving them waiting forever.
const $subscriptionsLoaded = createStore(false)
    .on(getSubscriptionsFx.finally, () => true)
    .reset(logoutButtonClicked);

const checkAnySubscriptionByEmailFx = createEffect(async (email: string) => {
    try {
        const response = await SubscriptionApi.checkAnySubscriptionByEmail(email);
        return response.subscriber;
    } catch (error) {
        return false;
    }
});

sample({
    clock: checkAnySubscriptionByEmailFx.doneData,
    fn: (subscriber) => subscriber,
    target: $hasSubscriptionByEmail
});

sample({
    clock: checkAnySubscriptionByEmailFx.doneData,
    fn: () => true,
    target: $subscriptionCheckCompleted
});

sample({
    clock: checkAnySubscriptionByEmailFx.fail,
    fn: () => false,
    target: $hasSubscriptionByEmail
});

sample({
    clock: checkAnySubscriptionByEmailFx.fail,
    fn: () => true,
    target: $subscriptionCheckCompleted
});

const cancelSubscriptionFx = createEffect(async (subId: string) => {
    const response = await SubscriptionApi.cancelSubscriptipn(subId);

    return response;
});

// Cancelling a paused subscription makes the backend wait for Solidgate's resume
// webhook before requesting the cancellation, so this request routinely runs for
// a few seconds — long enough that the wizard's final screen needs a real
// in-progress state instead of an optimistic "canceled".
const $isCancellingSubscription = cancelSubscriptionFx.pending;

const restoreSubscriptionFx = createEffect(async (subId: string) => {
    const response = await SubscriptionApi.restoreSubscription(subId);

    return response;
});

const getRestorePreviewFx = createEffect(async () => {
    return await SubscriptionApi.getRestorePreview();
});

const pauseSubscriptionFx = createEffect(async (body: PauseSubscriptionRequest) => {
    return await SubscriptionApi.pauseSubscription(body);
});

const resumePausedSubscriptionFx = createEffect(async () => {
    return await SubscriptionApi.resumePausedSubscription();
});

const $pausePollingExpectedStatus = createStore<"paused" | "active" | null>(null)
    .on(startPauseStatusPolling, (_, status) => status)
    .reset(stopPauseStatusPolling, logoutButtonClicked);

const $pausePollTickCount = createStore<number>(0).reset(startPauseStatusPolling);

const $pauseUntil = createStore<string | null>(null)
    .on(pauseSubscriptionFx.doneData, (_, response) => response.pauseUntil)
    .reset(resumePausedSubscriptionFx.done, logoutButtonClicked);

const $subscriptionPauseError = createStore<string | null>(null)
    .on(pauseSubscriptionFx.failData, (_, error) => getSubscriptionPauseErrorMessage(error))
    .on(resumePausedSubscriptionFx.failData, (_, error) => getSubscriptionPauseErrorMessage(error))
    .reset([
        pauseSubscriptionClicked,
        resumePausedSubscriptionClicked,
        subscriptionPauseErrorDismissed,
        logoutButtonClicked
    ]);

const $isPauseStatusPolling = createStore(false)
    .on(startPauseStatusPolling, () => true)
    .reset(stopPauseStatusPolling, logoutButtonClicked);

// The unsubscribe wizard shows its success screen as soon as it reaches the last
// step, which is also what triggers the cancel request — so a failed request has
// to be surfaced there instead of silently leaving the user believing they
// cancelled.
const $hasCancelSubscriptionFailed = createStore(false)
    .on(cancelSubscriptionFx.fail, () => true)
    .reset([
        cancelSubscriptionButtonClicked,
        cancelSubscriptionWithReasonsClicked,
        cancelSubscriptionFx.done,
        logoutButtonClicked
    ]);

const getUpdateCardFormFx = createEffect(async () => {
    return await SubscriptionApi.getUpdateCardForm();
});

const checkUpdateCardStatusFx = createEffect(async (orderId: string) => {
    return await SubscriptionApi.checkUpdateCardStatus(orderId);
});

const scheduleNextPoll = createEvent();

const delayedPoll = delay({
    source: scheduleNextPoll,
    timeout: 2000
});

const $restoreStatus = createStore<RestoreStatus>("idle")
    .on(restoreSubscriptionButtonClicked, () => "processing" as const)
    // The backend refuses to restore a UPI subscription whose AutoPay mandate
    // the customer revoked: nothing to charge, the student has to subscribe
    // again. Every other refusal is a plain failure.
    .on(restoreSubscriptionFx.failData, (_, error) =>
        isMandateRevokedError(error) ? ("mandate_revoked" as const) : ("failed" as const)
    )
    .on(restoreFailureDetected, () => "failed" as const)
    .reset(restoreDialogClosed, logoutButtonClicked);

const $restoredSubscription = createStore<ISubscription | null>(null).reset(
    restoreDialogClosed,
    logoutButtonClicked,
    restoreSubscriptionButtonClicked
);

// Billing method of the subscription being restored, as the backend read it
// from Solidgate when it accepted the restore. The outcome rules (card vs UPI)
// and the result copy branch on it.
const $restorePaymentType = createStore<string | null>(null)
    .on(restoreSubscriptionFx.doneData, (_, response: RestoreSubscriptionResponse | void) => response?.paymentType ?? null)
    .reset(restoreSubscriptionButtonClicked, restoreDialogClosed, logoutButtonClicked);

const trackSubscriptionRestoredFx = createEffect(async ({ subId, provider }: { subId: string; provider?: string }) => {
    return subscriptionRestored(subId, provider);
});

// The screen's terms come from the restore preview (Solidgate's renewal price
// and dates); until it answers the screen is loading and shows no price.
export type RestoreConfirmation = {
    subscription: ISubscription;
    state: RestoreState;
    terms: RestoreTerms;
    loading: boolean;
};

const findRestoreConfirmation = (
    subscriptions: ISubscription[],
    subId: string | null,
    preview: RestorePreview | null,
    loading: boolean
): RestoreConfirmation | null => {
    const subscription = subId ? subscriptions.find((sub) => sub.subscriptionId === subId) : undefined;
    if (!subscription) return null;

    const terms = restoreTermsOf(subscription, preview);
    return { subscription, state: terms.state, terms, loading };
};

// The preview answer of the open confirmation, null until it lands (or when
// the call failed — the local row stands in then). Both land through one
// event so the "presented" tracking below reads them after the stores did.
const restorePreviewSettled = createEvent<RestorePreview | null>();

sample({ clock: getRestorePreviewFx.doneData, target: restorePreviewSettled });
sample({ clock: getRestorePreviewFx.fail, fn: () => null, target: restorePreviewSettled });

const $restorePreview = createStore<RestorePreview | null>(null)
    .on(restorePreviewSettled, (_, preview) => preview)
    .reset(restoreSubscriptionRequested, logoutButtonClicked);

const $isRestorePreviewSettled = createStore(false)
    .on(restorePreviewSettled, () => true)
    .reset(restoreSubscriptionRequested, logoutButtonClicked);

// The subscription whose restore is waiting for the student's confirmation,
// with what the restore would do (see restoreTermsOf). Null = no screen.
// Closed (reset) only by restoreConfirmed / restoreConfirmationCancelled
// below, which the tracking samples emit AFTER reading the payload: a reset
// on the raw click events ran before the samples' read (effector orders the
// graph by declaration, not by "source before reset"), so the confirm and
// cancel clicks were never tracked.
const $restoreConfirmationSubId = createStore<string | null>(null)
    .on(restoreSubscriptionRequested, (_, subId) => subId)
    .reset(logoutButtonClicked);

const $restoreConfirmation = combine(
    $restoreConfirmationSubId,
    $subscriptions,
    $restorePreview,
    $isRestorePreviewSettled,
    (subId, subscriptions, preview, settled) => findRestoreConfirmation(subscriptions, subId, preview, !settled)
);

const restoreStepPayload = (confirmation: RestoreConfirmation | null): RestoreStepPayload | null =>
    confirmation ? { subId: confirmation.subscription.subscriptionId, restoreState: confirmation.state } : null;

// The analytics payload of the open confirmation; null without one. The
// tracking effects accept the null so the samples below need no type guard.
const $restoreStepPayload = $restoreConfirmation.map(restoreStepPayload);

const trackRestoreStep = (track: (payload: RestoreStepPayload) => void) =>
    createEffect((payload: RestoreStepPayload | null) => {
        if (payload) track(payload);
    });

const trackRestoreClickedFx = trackRestoreStep(subscriptionRestoreClicked);
const trackRestoreConfirmationPresentedFx = trackRestoreStep(restoreConfirmationPresented);
const trackRestoreConfirmClickedFx = trackRestoreStep(restoreConfirmClicked);
const trackRestoreCancelClickedFx = trackRestoreStep(restoreCancelClicked);

const tarckSubscriptionCanceledFx = createEffect(
    async ({
        subId,
        provider,
        cancellation_reason
    }: {
        subId: string;
        provider?: string;
        cancellation_reason?: string[];
    }) => {
        return subscriptionCanceled(subId, provider, cancellation_reason);
    }
);

const redirectToCustomerPortalFx = createEffect(async () => {
    try {
        const { url }: Awaited<{ url: string }> = await HttpRequest.do(
            `${process.env.NEXT_PUBLIC_API_URL}/subscription/portal`
        );
        window.location.href = url;
    } catch (error) {
        throw error;
    }
});

const setKnownUserCountryFx = createEffect(setKnownUserCountry);

// subscription.country is a backend join that is frequently empty (most
// truegate subscriptions, early-2025 solidgate ones). When the loaded
// subscriptions carry no country, fall back to the live GeoIP lookup so the
// "US$"/"$" notation still resolves for Spain/LatAm users. Registered as the
// lower-priority tier in helpers (setGeoUserCountry), so a later subscriptions
// refetch that DOES carry a country still wins regardless of arrival order.
const fetchGeoCountryFallbackFx = createEffect(async () => {
    const location = await UserApi.getUserLocation().catch(() => null);
    setGeoUserCountry(location?.country ?? "");
});

const $isRestoringSubscription = combine(
    restoreSubscriptionFx.pending,
    $pollingSubId,
    (isPending, subId) => isPending || !!subId
);

const $isSubscriptionPauseActionPending = combine(
    pauseSubscriptionFx.pending,
    resumePausedSubscriptionFx.pending,
    $isPauseStatusPolling,
    (isPausing, isResuming, isPolling) => isPausing || isResuming || isPolling
);

sample({
    clock: getSubscriptionsFx.doneData,
    target: $subscriptions
});

sample({
    clock: $subscriptions,
    filter: (subscriptions) => subscriptions.some((sub) => sub.country?.trim()),
    fn: (subscriptions) => subscriptions.find((sub) => sub.country?.trim())!.country!,
    target: setKnownUserCountryFx
});

// One-shot: the restore/pause pollers refetch subscriptions repeatedly — the
// geo lookup must not re-fire on every poll tick.
const $geoCountryFallbackRequested = createStore(false).on(fetchGeoCountryFallbackFx, () => true);

// Fired alongside the FIRST subscriptions request rather than after it:
// getCurrencySymbol is not reactive, so the geo country must be known before
// the first price render (plans resolve after subscriptions, geo is faster).
// Harmless when subscription.country later arrives — that tier wins anyway.
sample({
    clock: getSubscriptionsFx,
    source: $geoCountryFallbackRequested,
    filter: (alreadyRequested) => !alreadyRequested,
    target: fetchGeoCountryFallbackFx
});

sample({
    clock: solidgateUpsellPaymentCapabilityRequested,
    source: {
        hasRecurringToken: $solidgateUpsellHasRecurringToken,
        isPending: $isSolidgateUpsellPaymentCapabilityPending
    },
    filter: ({ hasRecurringToken, isPending }) => hasRecurringToken === null && !isPending,
    target: getSolidgateUpsellPaymentCapabilityFx
});

sample({
    clock: manageFfSubscriptionClicked,
    target: redirectToCustomerPortalFx
});

sample({
    clock: cancelSubscriptionButtonClicked,
    target: cancelSubscriptionFx
});

sample({
    clock: cancelSubscriptionButtonClicked,
    source: $subscriptions,
    fn: (subscriptions, clickedId) => {
        const found = subscriptions.find((sub) => sub.subscriptionId === clickedId);
        return found?.endDate || "";
    },
    target: $subscriptionEndDate
});

sample({
    clock: cancelSubscriptionFx.doneData,
    target: [getSubscriptionsFx]
});

sample({
    clock: pauseSubscriptionClicked,
    target: pauseSubscriptionFx
});

sample({
    clock: resumePausedSubscriptionClicked,
    target: resumePausedSubscriptionFx
});

sample({
    clock: pauseSubscriptionFx.done,
    fn: () => "paused" as const,
    target: startPauseStatusPolling
});

sample({
    clock: resumePausedSubscriptionFx.done,
    fn: () => "active" as const,
    target: startPauseStatusPolling
});

sample({
    clock: pauseStatusPollTick,
    target: getSubscriptionsFx
});

sample({
    clock: pauseStatusPollTick,
    source: $pausePollTickCount,
    fn: (count) => count + 1,
    target: $pausePollTickCount
});

sample({
    clock: $pausePollTickCount,
    filter: (count) => count >= 15,
    target: stopPauseStatusPolling
});

sample({
    clock: $subscriptions,
    fn: (subscriptions) => {
        if (subscriptions.length === 0) {
            if (typeof window !== "undefined") {
                const savedPaymentSystem = localStorage.getItem("purchase_payment_system");
                if (
                    savedPaymentSystem === "solidgate" ||
                    savedPaymentSystem === "ecommpay" ||
                    savedPaymentSystem === "truegate"
                ) {
                    return savedPaymentSystem;
                }
            }
            return "other_payment_system";
        }

        const paymentSystem = subscriptions[0].platform.code;
        if (typeof window !== "undefined") {
            localStorage.setItem("purchase_payment_system", paymentSystem);
        }
        return paymentSystem;
    },
    target: setPaymentSystem
});

sample({
    clock: cancelSubscriptionWithReasonsClicked,
    source: combine({ subscriptions: $subscriptions, paymentSystem: $paymentSystem }),
    fn: ({ subscriptions, paymentSystem }, { subscriptionId, reasons }) => {
        return {
            subId: subscriptionId,
            provider: paymentSystem,
            cancellation_reason: reasons
        };
    },
    target: tarckSubscriptionCanceledFx
});

sample({
    clock: cancelSubscriptionWithReasonsClicked,
    fn: ({ subscriptionId }) => subscriptionId,
    target: cancelSubscriptionFx
});

sample({
    clock: cancelSubscriptionWithReasonsClicked,
    source: $subscriptions,
    fn: (subscriptions, { subscriptionId }) => {
        const found = subscriptions.find((sub) => sub.subscriptionId === subscriptionId);
        return found?.endDate || "";
    },
    target: $subscriptionEndDate
});

sample({
    clock: restorePollTick,
    source: $restorePollTickCount,
    fn: (n) => n + 1,
    target: $restorePollTickCount
});

sample({
    clock: $restorePollTickCount,
    filter: (n) => n >= 15,
    target: stopRestorePolling
});

sample({
    clock: restorePollTick,
    target: getSubscriptionsFx
});

sample({
    clock: getSubscriptionsFx.doneData,
    source: $pollingSubId,
    filter: (subId, subs) =>
        !!subId && !!subs?.find((s) => s.subscriptionId === subId)?.isAutoRenewable,
    fn: () => true,
    target: $restoreActivationSeen
});

// Restored subscription flipped back to non-renewable (charge failed on
// Solidgate side) — fail fast instead of hanging until the polling timeout.
sample({
    clock: getSubscriptionsFx.doneData,
    source: { subId: $pollingSubId, activationSeen: $restoreActivationSeen, restoreStatus: $restoreStatus },
    filter: ({ subId, activationSeen, restoreStatus }, subs) => {
        if (!subId || !activationSeen || restoreStatus !== "processing") return false;
        const sub = subs?.find((s) => s.subscriptionId === subId);
        return !!sub && !sub.isAutoRenewable;
    },
    target: [restoreFailureDetected, stopRestorePolling]
});

sample({
    clock: $restoredSubscription,
    filter: (sub): sub is ISubscription => !!sub,
    fn: () => "success" as const,
    target: $restoreStatus
});

sample({
    clock: $restoredSubscription,
    filter: (sub): sub is ISubscription => !!sub,
    target: stopRestorePolling
});

// Polling ran out without a confirmation: a card charge that did not go
// through, or a late restore webhook on a UPI subscription (pending, not failed).
sample({
    clock: stopRestorePolling,
    source: { status: $restoreStatus, paymentType: $restorePaymentType },
    filter: ({ status }) => status === "processing",
    fn: ({ paymentType }) => restoreTimeoutStatus(paymentType),
    target: $restoreStatus
});

sample({
    clock: restoreSubscriptionButtonClicked,
    target: restoreSubscriptionFx
});

// The Restore click and the confirmation screen are one moment: it opens at
// once, loading, and asks the backend what the restore would do.
const restoreConfirmationOpened = sample({
    clock: restoreSubscriptionRequested,
    source: $subscriptions,
    fn: (subscriptions, subId) => findRestoreConfirmation(subscriptions, subId, null, true)
});

sample({
    clock: restoreConfirmationOpened,
    filter: (confirmation) => !!confirmation,
    fn: restoreStepPayload,
    target: trackRestoreClickedFx
});

sample({ clock: restoreSubscriptionRequested, target: getRestorePreviewFx });

// The screen counts as presented once its terms are known: the preview (or
// the local fallback) decides the restore_state the event carries. Read from
// the stores the event itself updated, not from the derived payload, so the
// order never depends on declaration.
const restoreConfirmationPresentedWithTerms = sample({
    clock: restorePreviewSettled,
    source: { subId: $restoreConfirmationSubId, subscriptions: $subscriptions },
    fn: ({ subId, subscriptions }, preview) => restoreStepPayload(findRestoreConfirmation(subscriptions, subId, preview, false))
});

sample({ clock: restoreConfirmationPresentedWithTerms, target: trackRestoreConfirmationPresentedFx });

// The confirmation as it stood when the button was pressed, read first; the
// store is reset from these events, so the read can never come after it.
const restoreConfirmed = sample({
    clock: restoreSubscriptionButtonClicked,
    source: $restoreStepPayload,
    fn: (payload) => payload
});

const restoreConfirmationCancelled = sample({
    clock: restoreConfirmationDismissed,
    source: $restoreStepPayload,
    fn: (payload) => payload
});

sample({ clock: restoreConfirmed, target: trackRestoreConfirmClickedFx });
sample({ clock: restoreConfirmationCancelled, target: trackRestoreCancelClickedFx });

$restoreConfirmationSubId.reset(restoreConfirmed, restoreConfirmationCancelled);
$restorePreview.reset(restoreConfirmed, restoreConfirmationCancelled);
$isRestorePreviewSettled.reset(restoreConfirmed, restoreConfirmationCancelled);

sample({
    clock: restoreSubscriptionFx.done,
    fn: ({ params }) => params,
    target: startRestorePolling
});

sample({
    clock: $restoredSubscription,
    source: $paymentSystem,
    filter: (_, sub) => !!sub,
    fn: (provider, sub) => ({ subId: sub!.subscriptionId, provider }),
    target: trackSubscriptionRestoredFx
});

sample({
    clock: changePaymentMethodClicked,
    fn: () => { },
    target: getUpdateCardFormFx
});

sample({
    clock: getUpdateCardFormFx.doneData,
    target: $updateCardFormResponse
});

sample({
    clock: getUpdateCardFormFx.doneData,
    fn: (response) => response.order_id,
    target: $updateCardOrderId
});

sample({
    clock: getUpdateCardFormFx.doneData,
    fn: () => "form_ready" as const,
    target: $changePaymentMethodStatus
});

sample({
    clock: getUpdateCardFormFx.fail,
    fn: () => "error",
    target: changePaymentMethodFailed
});

sample({
    clock: widgetPaymentSuccess,
    source: $updateCardOrderId,
    filter: (orderId): orderId is string => orderId !== null,
    target: checkUpdateCardStatusFx
});

sample({
    clock: delayedPoll,
    source: { orderId: $updateCardOrderId, status: $changePaymentMethodStatus },
    filter: ({ orderId, status }) => orderId !== null && status === "polling",
    fn: ({ orderId }) => orderId as string,
    target: checkUpdateCardStatusFx
});

sample({
    clock: checkUpdateCardStatusFx,
    source: $updateCardPollCount,
    fn: (n) => n + 1,
    target: $updateCardPollCount
});

sample({
    clock: checkUpdateCardStatusFx.doneData,
    source: $updateCardPollCount,
    filter: (count, response) => response.status === "pending" && count < 20,
    target: scheduleNextPoll
});

sample({
    clock: checkUpdateCardStatusFx.doneData,
    source: $updateCardPollCount,
    filter: (count, response) => response.status === "pending" && count >= 20,
    fn: () => "error",
    target: changePaymentMethodFailed
});

sample({
    clock: checkUpdateCardStatusFx.doneData,
    filter: (response) => response.status === "success",
    target: changePaymentMethodSuccess
});

sample({
    clock: checkUpdateCardStatusFx.doneData,
    filter: (response) => response.status === "failed",
    fn: () => "error",
    target: changePaymentMethodFailed
});

sample({
    clock: checkUpdateCardStatusFx.fail,
    fn: () => "error",
    target: changePaymentMethodFailed
});

const contract = new ModelContract(
    {
        $subscriptions,
        $subscriptionsLoaded,
        $hasSubscriptionByEmail,
        $subscriptionCheckCompleted,
        $subscriptionEndDate,
        $paymentSystem,
        $solidgateUpsellHasRecurringToken,
        $isSolidgateUpsellPaymentCapabilityPending,
        $solidgateUpsellPaymentCapabilityError,
        $isRestoringSubscription,
        $isSubscriptionPauseActionPending,
        $pauseUntil,
        $subscriptionPauseError,
        $hasCancelSubscriptionFailed,
        $isCancellingSubscription,
        $restoreStatus,
        $restoredSubscription,
        $restorePaymentType,
        $restoreConfirmation,
        $changePaymentMethodSubscriptionId,
        $updateCardFormResponse,
        $changePaymentMethodStatus,
        $changePaymentMethodError,
        $isPausedSubscriptionGateActive
    },
    {
        manageFfSubscriptionClicked,
        cancelSubscriptionButtonClicked,
        cancelSubscriptionWithReasonsClicked,
        restoreSubscriptionButtonClicked,
        pauseSubscriptionClicked,
        resumePausedSubscriptionClicked,
        subscriptionPauseErrorDismissed,
        restoreDialogClosed,
        restoreSubscriptionRequested,
        restoreConfirmationDismissed,
        setPaymentSystem,
        subscriptionScreenOpened,
        solidgateUpsellPaymentCapabilityRequested,
        changePaymentMethodClicked,
        changePaymentMethodModalClosed,
        changePaymentMethodSuccess,
        changePaymentMethodFailed,
        widgetPaymentSuccess
    },
    {
        getSubscriptionsFx,
        getSolidgateUpsellPaymentCapabilityFx,
        checkAnySubscriptionByEmailFx,
        restoreSubscriptionFx,
        getRestorePreviewFx,
        cancelSubscriptionFx,
        pauseSubscriptionFx,
        resumePausedSubscriptionFx,
        getUpdateCardFormFx,
        checkUpdateCardStatusFx
    },
    {}
);

export type ISubscriptionContract = typeof contract;

export class SubscriptionModule extends ModuleDescription<ISubscriptionContract> {
    constructor(public authModule: IUserAuthContract) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: [appMounted, routePathNameChanged, this.authModule.stores.$student.updates],
            source: {
                student: this.authModule.stores.$student,
                pathname: $activeRoute
            },
            fn: ({ student, pathname }) => {
                if (!isPausedSubscriptionStatus(student?.status)) {
                    return false;
                }

                return !isPausedSubscriptionAllowedPath(pathname);
            },
            target: $isPausedSubscriptionGateActive
        });

        sample({
            clock: [appMounted, routePathNameChanged, this.authModule.stores.$student.updates],
            source: {
                student: this.authModule.stores.$student,
                pathname: $activeRoute,
                routeHistory: $pausedGateRouteHistory
            },
            filter: ({ student, pathname }) => {
                if (student === null) return false;
                if (!isPausedSubscriptionStatus(student.status)) return false;

                return !isPausedSubscriptionAllowedPath(pathname);
            },
            fn: ({ routeHistory }) =>
                isPausedSubscriptionAllowedPath(routeHistory.prev)
                    ? PAUSED_SUBSCRIPTION_IN_APP_REDIRECT_PATH
                    : PAUSED_SUBSCRIPTION_REDIRECT_PATH,
            target: routerReplace
        });

        sample({
            clock: this.authModule.stores.$student,
            filter: (student) => student !== null,
            target: getSubscriptionsFx
        });

        sample({
            clock: subscriptionScreenOpened,
            source: this.authModule.stores.$student,
            filter: (student) => student !== null,
            target: getSubscriptionsFx
        });

        sample({
            clock: restorePollTick,
            target: this.authModule.events.studentProfileRefreshed
        });

        sample({
            clock: pauseStatusPollTick,
            target: this.authModule.events.studentProfileRefreshed
        });

        // Cancelling a paused subscription flips the student out of "paused" on
        // the backend, so the profile has to be refetched — otherwise the stale
        // "paused" status keeps the paused screen and its gate in place until the
        // next reload.
        sample({
            clock: cancelSubscriptionFx.done,
            target: this.authModule.events.studentProfileRefreshed
        });

        sample({
            clock: this.authModule.stores.$student.updates,
            source: {
                expectedStatus: $pausePollingExpectedStatus,
                isPolling: $isPauseStatusPolling
            },
            filter: ({ expectedStatus, isPolling }, student) =>
                isPolling && !!expectedStatus && student?.status === expectedStatus,
            target: stopPauseStatusPolling
        });

        sample({
            clock: getSubscriptionsFx.doneData,
            source: { subId: $pollingSubId, student: this.authModule.stores.$student, paymentType: $restorePaymentType },
            filter: ({ subId, student, paymentType }, subs) => {
                if (!subId) return false;
                if (student?.status === "redemption") return false;
                const s = subs?.find((x) => x.subscriptionId === subId);
                return !!s && isRestoreConfirmed(s, paymentType);
            },
            fn: ({ subId }, subs) => subs.find((s) => s.subscriptionId === subId) ?? null,
            target: $restoredSubscription
        });

        sample({
            clock: this.authModule.stores.$student.updates,
            source: { restoreStatus: $restoreStatus, pollingSubId: $pollingSubId },
            filter: ({ restoreStatus, pollingSubId }, student) =>
                restoreStatus === "processing" && !!pollingSubId && student?.status === "redemption",
            target: [restoreFailureDetected, stopRestorePolling]
        });
    }
}
